import { useEffect, useState, useMemo, useCallback } from 'react';
import { Grid, TextField, Button, Select, MenuItem, FormControl, InputLabel, Box, Typography } from '@mui/material';
import { userListColumns } from '../../component/common/tableHelper';
import useApiService from '../../component/hooks/useApiService';
import Filter from '../../component/ui/filter/filter';
import { useDialog } from '../../context/dialog/DialogContext';
import AddIcon from '@mui/icons-material/Add';
import { TIER_TYPES } from '../../constans/parameter';
import DataGrid from '../../component/ui/data-grid/DataGrid';
import { AdminAndUserStatusActions } from '../../constans/enum';
import { apiUrls } from '../../constans/apiUrls';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';
import AddUser from './AddUser';

const UserList = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({});
  const { showSnackbar } = useAppSnackbar();
  const { openDialog } = useDialog();
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const handleClick = () => {
    openDialog({
      title: "Yeni Kullanıcı Ekle",
      maxWidth: 'sm',
      content: <AddUser openType={"Create"} />,
      callback: (response) => {
        if (response.id) {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          fetchData();
          showSnackbar('Kullanıcı eklendi.', 'info');
        }
      },
    });

  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { executeGet } = useApiService();
  // Open user details view when clicking on admin_id cell
  const onViewClicked = useCallback(
    (data) => {
      openDialog({
        title: "Kullanıcı Bilgisi",
        MaxWidth: 'xl',
        content: <AddUser openType={"View"} data={data} />,
        callback: () => { fetchData(filter) },
      });
    },
    [filter]
  );
  const columns = useMemo(() => userListColumns(), []);

  const fetchData = async (filterParams = filter, page = paginationModel.page, pageSize = paginationModel.pageSize) => {
    setLoading(true);
    try {
      const baseParams = {
        ...filterParams,
        offset: page * pageSize,
        limit: pageSize,
      };
      const params = Object.fromEntries(Object.entries(baseParams).filter(([_, v]) => v !== '' && v !== null && v !== undefined));
      const response = await executeGet({
        url: apiUrls.User,
        params,
      });

      setRows(response.items ?? []);
      setTotalCount(response.meta?.total ?? 0);
    } catch (err) {
      console.error('Veriler getirilemedi:', err);
      setError('Veriler getirilemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filter, paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleReset = () => {
    const emptyFilter = {};
    setFilter(emptyFilter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (paginationModel.page === 0) {
      fetchData({}, 0);
    }
  };

  const onChangeRequestClicked = useCallback(
    (data) => {
      openDialog({
        title: "Kullanıcı Bilgisi Güncelle",
        maxWidth: 'sm',
        content: <AddUser data={data} openType={"Update"} adminPanelAction={AdminAndUserStatusActions.CHANGE_REQUESTED} />,
        callback: (response) => {
          if (response === true) {
            const emptyFilter = {};
            setFilter(emptyFilter);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            fetchData({});
            showSnackbar('Kullanıcı bilgisi güncellendi.', 'info');
          }
        },
      });
    },
    [filter]
  );

  const onChangePasswordClicked = useCallback(
    (data) => {
      openDialog({
        title: "Şifreyi Güncelle",
        maxWidth: 'sm',
        content: <AddUser data={data} openType={"PasswordUpdate"} adminPanelAction={AdminAndUserStatusActions.USER_CHANGE_PASSWORD} />,
        callback: (response) => {
          if (response === true) {
            fetchData(filter, paginationModel.page, paginationModel.pageSize);
            showSnackbar('Kullanıcı şifresi güncellendi.', 'info');
          }
        },
      });
    },
    [filter, paginationModel.page, paginationModel.pageSize]
  );

  

  const onCancelClicked = useCallback(
    (data) => {
      const ConfirmContent = ({ close }) => (
        <Box>
          <Typography gutterBottom>
            Kullanıcı <b>silinecektir</b>. Onaylıyor musunuz?
          </Typography>
          <Typography variant="body2" color="warning.main" gutterBottom>
            Not: Kullanıcı silinince geri alınamayacaktır.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => close(false)}>Vazgeç</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                close(true);
                openDialog({
                  title: "Kullanıcı Silme",
                  maxWidth: "sm",
                  content: <AddUser openType={"Delete"} data={data} adminPanelAction={AdminAndUserStatusActions.DELETE_USER} />,
                  callback: (response) => {
                    if (response === true) {
                      const emptyFilter = {};
                      setFilter(emptyFilter);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      fetchData({});
                      showSnackbar('Kullanıcı silindi.', 'info');
                    }
                  },
                });
              }}
            >
              Onayla
            </Button>
          </Box>
        </Box>
      );
      openDialog({ title: 'Onay', maxWidth: 'xs', content: <ConfirmContent /> });
    },
    [filter]
  );

  const rowActions = useMemo(
    () => [
      {
        name: 'view',
        icon: 'view',
        tooltip: "Görüntüle",
        onClick: onViewClicked
      },
      {
        name: 'changeRequest',
        icon: "edit",
        tooltip: "Düzenle",
        onClick: onChangeRequestClicked,
      },
      {
        name: 'changePassword',
        icon: "editPassword",
        tooltip: "Şifreyi Güncelle",
        onClick: onChangePasswordClicked,
      },
      {
        name: 'cancelLetterCredit',
        icon: "cancel",
        tooltip: "Sil",
        onClick: onCancelClicked,
      },
    ],
    [filter]
  );

  const onRowActionVisible = (row, action) => {
    switch (action) {
      case 'view':
        return true;
      case 'changeRequest':
        return true;
      case 'changePassword':
        return true;
      case 'cancelLetterCredit':
        return true;
      default:
        return false;
    }
  };

  return (
    <div style={{ padding: '0 1rem 1rem' }}>
      <h2>Kullanıcı Listesi</h2>
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <Filter
        onFilter={() => fetchData(filter)}
        onFilterReset={handleReset}
        rightAction={(
          <Button onClick={handleClick} variant="contained" startIcon={<AddIcon />} sx={{
            textTransform: 'none',
            backgroundColor: '#1976d2',
            color: 'white',
            borderRadius: '10px',
            fontWeight: 'bold',
          }}>Yeni Kullanıcı Ekle</Button>
        )}
      >
        <Grid item xs={4}>
          <TextField
            fullWidth
            name="Email"
            label="Email"
            variant="outlined"
            value={filter?.email || ''}
            onChange={(e) =>
              setFilter({ ...filter, email: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            name="admin_email"
            label="Admin Email"
            variant="outlined"
            value={filter?.admin_email || ''}
            onChange={(e) => setFilter({ ...filter, admin_email: e.target.value })}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl sx={{ minWidth: 220 }} variant="outlined">
            <InputLabel id="tier-type-label" shrink>Kullanıcı Kategorisi</InputLabel>
            <Select
              labelId="tier-type-label"
              id="tier-type"
              value={filter?.tier_id || ''}
              onChange={(e) =>
                setFilter({ ...filter, tier_id: e.target.value })
              }
              label="Kullanıcı Kategorisi"
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: 'rgba(0,0,0,0.38)' }}>Kategori seçiniz</span>;
                }
                const item = TIER_TYPES.find((t) => t.Code === selected);
                return item ? item.ValueText1 : selected;
              }}
            >
              <MenuItem value="" disabled>
                Kategori seçiniz
              </MenuItem>

              {TIER_TYPES.map((type) => (
                <MenuItem key={type.Code} value={type.Code}>
                  {type.ValueText1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            name="meta_id"
            label="Meta ID"
            variant="outlined"
            value={filter?.meta_id ?? ''}
            onChange={(e) => setFilter({ ...filter, meta_id: e.target.value })}
          />
        </Grid>

        

        {/* <Grid item xs={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
            <DatePicker
              label="Oluşturulma Tarihi"
              value={filter?.LCIssueStartDate ? dayjs(filter.LCIssueStartDate) : null}
              onChange={(e) => {
                setFilter({ ...filter, LCIssueStartDate: e ? e.toISOString() : null });
              }}
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: {
                    '& .MuiInputAdornment-root': {
                      marginLeft: '20px',
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Grid> */}
      </Filter>
      <div style={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          rowActions={rowActions}
          columns={columns}
          onRowActionVisible={onRowActionVisible}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          loading={loading}
          getRowId={(row, index) => row.id ?? index + 1}
        />
      </div>
    </div>
  );
};

export default UserList;
